import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BaseModal from "../../components/BaseModal";
import BaseButton from "../../components/BaseButton";
import BaseIcon from "../../components/BaseIcon";
import { MODAL_TYPES } from "../../utils/emitter";
import useModalActions from "../../hooks/useModalActions";
import Pricing from "../../store/Pricing";
import Points from "../../store/Points";
import PersonalAccount from "../../store/PersonalAccount";
import ROUTES from "../../utils/routes";
import "./style.scss";

// «Хочешь увидеть весь тест полностью?» — попап демо-результатов с ДВУМЯ тарифами
// (Figma 12414-50766 web / 12414-50754 mobile): тест-88 ($9/✦900) и полная игра
// ($49/✦4900) + пояснительный блок «Обрати внимание!». Логика покупки — как в
// BuyProductModal, но параметризована по товару. Цены доменные (Pricing.format).
// payload: { gameId?, from?, onPurchased? }
export default observer(function SeeFullTestModal() {
  const [payload, setPayload] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModalActions();

  useEffect(() => {
    Pricing.fetch();
  }, []);

  const onModalPayload = (data) => {
    if (!data) return;
    setPayload(data);
    if (Points.balance === null) Points.fetchBalance();
  };

  const balanceLoaded = Points.balance !== null;

  const buyWithMoney = (productType) => {
    closeModal(MODAL_TYPES.SEE_FULL_TEST);
    navigate(ROUTES.PAYMENT_PAGE, {
      state: { productType, gameId: payload?.gameId, from: payload?.from },
    });
  };

  const buyWithPoints = (productType) => {
    if (!balanceLoaded) return;
    const price = Pricing.get(productType);
    const enough = price && price.points !== null && Points.balance >= price.points;
    if (!enough) {
      closeModal(MODAL_TYPES.SEE_FULL_TEST);
      openModal(MODAL_TYPES.NOT_ENOUGH_POINTS, { productType });
      return;
    }
    // «полная игра» = апгрейд демо → шлём DEMO_GAME (зеркально денежному пути)
    Points.spend(
      productType === "GAME" ? "DEMO_GAME" : productType,
      { gameId: payload?.gameId },
      async () => {
        closeModal(MODAL_TYPES.SEE_FULL_TEST);
        await PersonalAccount.getPersonalAccountData();
        payload?.onPurchased && payload.onPurchased();
      },
      (error) => {
        closeModal(MODAL_TYPES.SEE_FULL_TEST);
        const code = error?.response?.data?.message?.code;
        if (code === "INSUFFICIENT_POINTS") {
          openModal(MODAL_TYPES.NOT_ENOUGH_POINTS, { productType });
        } else if (code === "CONNECT_REQUIRED") {
          openModal(MODAL_TYPES.CREATE_TEST_INFO);
        } else if (code === "FULFILLMENT_FAILED") {
          openModal(MODAL_TYPES.PURCHASE_FAILED);
        }
      }
    );
  };

  const renderActions = (productType) => {
    const price = Pricing.get(productType);
    const canPayWithPoints = Boolean(price && price.points !== null);
    const enoughPoints =
      canPayWithPoints && balanceLoaded && Points.balance >= price.points;
    return (
      <div className="see-full-test-modal__actions">
        <BaseButton onClick={() => buyWithMoney(productType)}>
          {t("loyalty.buy_for", { price: Pricing.format(productType) })}
        </BaseButton>
        {canPayWithPoints ? (
          <BaseButton
            onClick={() => buyWithPoints(productType)}
            // item 3 (Figma 11688-43119): хватает баллов — обводка с градиентной
            // рамкой + градиентная звезда (кликабельна); не хватает — серая, не кликается.
            unfilledGradient={enoughPoints}
            unfilledGray={!enoughPoints}
            disabled={!enoughPoints || Points.isSpending || !balanceLoaded}
          >
            <span className="see-full-test-modal__points">
              <BaseIcon
                name="star"
                className={`see-full-test-modal__points-star${
                  enoughPoints ? "" : " see-full-test-modal__points-star--muted"
                }`}
              />
              {price.points}
            </span>
          </BaseButton>
        ) : null}
      </div>
    );
  };

  const priceTest = Pricing.format("REPEAT_TEST");
  const priceExt = Pricing.format("EXTENDED_57");
  const priceFull = Pricing.format("GAME");

  return (
    <BaseModal
      className="see-full-test-modal"
      eventTypeModal={MODAL_TYPES.SEE_FULL_TEST}
      onPayload={onModalPayload}
      title={t("loyalty.products.GAME.title")}
      description={t("loyalty.products.GAME.description")}
      descriptionStyle="see-full-test-modal__subtitle"
    >
      {payload ? (
        <div className="see-full-test-modal__body">
          {renderActions("REPEAT_TEST")}

          <div className="see-full-test-modal__notice">
            <div className="see-full-test-modal__notice-head">
              {t("see_full_test.notice_head")}
            </div>
            <p>{t("see_full_test.p1", { price: priceTest })}</p>
            <p>{t("see_full_test.p2", { price: priceExt })}</p>
            <p className="see-full-test-modal__notice-full">
              {t("see_full_test.p3", { price: priceFull })}
            </p>
            <p>{t("see_full_test.p4")}</p>
            <p>{t("see_full_test.p5", { price: priceFull })}</p>
          </div>

          {renderActions("GAME")}
        </div>
      ) : null}
    </BaseModal>
  );
});
